// /mnt/mantenimientos/frontend/src/context/ClienteContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useState,
} from "react";
import {
  getClientes,
  getCliente,
  getOrdenadoresPorCliente,
  getMantenimientosPorCliente,
  getMantenimientosPorPc,
  crearMantenimiento,
  getPendientes,
  getZabbixDashboardCliente,
  getEffectiveAccess,
  verificarMantenimientoMes,
} from "../api";
import { getRol, isTecnico } from "../utils/auth";
import { MODULE_IDS } from "../modules/moduleIds";

const ClienteContext = createContext(null);
const CLIENT_CONTEXT_STORAGE_KEY = "app-client-context-id";

function initialClientContextId() {
  try { return localStorage.getItem(CLIENT_CONTEXT_STORAGE_KEY) || null; } catch { return null; }
}

/* =========================================================
   HELPERS
========================================================= */

function esDelMesActual(fechaISO) {
  if (!fechaISO) return false;
  const hoy = new Date();
  const fecha = new Date(fechaISO);
  return (
    fecha.getFullYear() === hoy.getFullYear() &&
    fecha.getMonth() === hoy.getMonth()
  );
}

function normalizeClientes(raw) {
  if (Array.isArray(raw)) return raw;

  if (raw && typeof raw === "object") {
    if (Array.isArray(raw.clientes)) return raw.clientes;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.rows)) return raw.rows;
    if (Array.isArray(raw.result)) return raw.result;
  }

  return [];
}

export function ClienteProvider({ children }) {
  /* =========================
     NAV
  ========================= */
  const [activeModule, setActiveModule] = useState(() =>
    MODULE_IDS.DASHBOARD
  );

  /* =========================
     BASE DATA
  ========================= */
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [effectiveAccess, setEffectiveAccess] = useState({
    loaded: false,
    moduleKeys: [],
    moduleLevels: {},
    allClients: false,
    clientIds: [],
  });

  // El cliente es un ámbito de trabajo transversal, no una pantalla distinta.
  // Se conserva entre módulos y recargas para evitar selecciones repetidas.
  const [clienteSeleccionadoId, setClienteSeleccionadoId] = useState(initialClientContextId);
  const [clienteInfo, setClienteInfo] = useState(null);
  const [clientContextHydrated, setClientContextHydrated] = useState(false);

  const [ordenadores, setOrdenadores] = useState([]);
  const [pcActivo, setPcActivo] = useState(null);
  const [maintenanceFocus, setMaintenanceFocus] = useState(null);

  /* =========================
     PENDIENTES
  ========================= */
  const [pendientes, setPendientes] = useState([]);
  const [agrupadosPendientes, setAgrupadosPendientes] = useState({});
  const [loadingPendientes, setLoadingPendientes] = useState(false);

  /* =========================
     ESTADO MANTENIMIENTOS
  ========================= */
  const [estado, setEstado] = useState({
    mantenimientosPorPc: {},
    formularios: {},
    infoAdicionalEdit: {},
  });

  /* =========================
     ZABBIX
  ========================= */
  const [zabbixByPc, setZabbixByPc] = useState({});
  const [loadingZabbix, setLoadingZabbix] = useState(false);

  const [loadingCliente, setLoadingCliente] = useState(false);

  /* =========================================================
     CLIENTES (LISTA GLOBAL)
  ========================================================= */

  const refreshClientes = useCallback(async () => {
    try {
      setLoadingClientes(true);
      const [clientsRes, accessRes] = await Promise.all([
        getClientes(),
        getEffectiveAccess(),
      ]);
      const access = accessRes.data || {};
      const allowedClientIds = new Set((access.client_ids || []).map(Number));
      const list = normalizeClientes(clientsRes?.data).filter(
        (client) => !isTecnico() || access.all_clients || allowedClientIds.has(Number(client.id))
      );
      setEffectiveAccess({
        loaded: true,
        moduleKeys: Array.isArray(access.module_keys) ? access.module_keys : [],
        moduleLevels: access.module_levels && typeof access.module_levels === "object" ? access.module_levels : {},
        allClients: Boolean(access.all_clients),
        clientIds: Array.isArray(access.client_ids) ? access.client_ids.map(Number) : [],
      });
      setClientes(list);
    } catch (err) {
      console.error("Error cargando clientes:", err);
      setClientes([]);
      setEffectiveAccess({ loaded: true, moduleKeys: [], moduleLevels: {}, allClients: false, clientIds: [] });
    } finally {
      setLoadingClientes(false);
    }
  }, []);

  useEffect(() => {
    refreshClientes();
  }, [refreshClientes]);

  // Los accesos pueden cambiar mientras el técnico sigue conectado.
  // Al volver a la pestaña se actualizan sin requerir volver a iniciar sesión.
  useEffect(() => {
    const refreshOnReturn = () => { if (document.visibilityState === 'visible') refreshClientes(); };
    window.addEventListener('focus', refreshOnReturn);
    document.addEventListener('visibilitychange', refreshOnReturn);
    return () => { window.removeEventListener('focus', refreshOnReturn); document.removeEventListener('visibilitychange', refreshOnReturn); };
  }, [refreshClientes]);

  /* =========================================================
     PENDIENTES
  ========================================================= */
  const refreshPendientes = useCallback(async () => {
    try {
      setLoadingPendientes(true);
      const res = await getPendientes();
      const data = res.data || [];

      const agrup = {};
      data.forEach((p) => {
        agrup[p.cliente] = agrup[p.cliente] || [];
        agrup[p.cliente].push(p.descripcion);
      });

      setPendientes(data);
      setAgrupadosPendientes(agrup);
    } catch (err) {
      console.error("Error obteniendo pendientes:", err);
      setPendientes([]);
      setAgrupadosPendientes({});
    } finally {
      setLoadingPendientes(false);
    }
  }, []);

  useEffect(() => {
    if (!effectiveAccess.loaded) return;
    if (isTecnico() && !effectiveAccess.moduleKeys.includes(MODULE_IDS.MANTENIMIENTOS)) {
      setPendientes([]);
      setAgrupadosPendientes({});
      return;
    }
    refreshPendientes();
  }, [effectiveAccess.loaded, effectiveAccess.moduleKeys, refreshPendientes]);

  /* =========================================================
     HELPERS
  ========================================================= */
  const tieneMantenimientoMesActual = useCallback(
    (pcId) => {
      const lista = estado.mantenimientosPorPc[pcId] || [];
      return lista.some(
        (m) => Number(m.realizado) === 1 && esDelMesActual(m.fecha)
      );
    },
    [estado.mantenimientosPorPc]
  );

  const clienteTagZabbix = useMemo(() => {
    return (
      clienteInfo?.codigo_cliente ||
      clienteInfo?.codigo ||
      clienteInfo?.tag_zabbix ||
      ""
    );
  }, [clienteInfo]);

  /* =========================================================
     ZABBIX
  ========================================================= */
  const refreshZabbixCliente = useCallback(
    async (tagOverride) => {
      const tag = String(tagOverride || clienteTagZabbix || "").trim();

      if (!tag) {
        setZabbixByPc({});
        return;
      }

      try {
        setLoadingZabbix(true);
        const res = await getZabbixDashboardCliente(tag);
        setZabbixByPc(res.data?.byPc || {});
      } catch (err) {
        console.warn("Zabbix error:", err);
        setZabbixByPc({});
      } finally {
        setLoadingZabbix(false);
      }
    },
    [clienteTagZabbix]
  );

  /* =========================================================
     REFRESH ORDENADORES
  ========================================================= */
  const refreshOrdenadores = useCallback(async () => {
    if (!clienteSeleccionadoId) return;

    try {
      const pcsRes = await getOrdenadoresPorCliente(clienteSeleccionadoId);
      const pcs = pcsRes.data || [];

      setOrdenadores(pcs);

      setPcActivo((prev) => {
        if (!prev) return pcs?.[0]?.id || null;
        const exists = pcs.some((p) => p.id === prev);
        return exists ? prev : pcs?.[0]?.id || null;
      });

      setEstado((prev) => {
        const nextInfo = { ...(prev.infoAdicionalEdit || {}) };
        pcs.forEach((pc) => {
          nextInfo[pc.id] = pc.info_adicional || "";
        });
        return { ...prev, infoAdicionalEdit: nextInfo };
      });
    } catch (err) {
      console.error("Error refrescando ordenadores:", err);
    }
  }, [clienteSeleccionadoId]);

  /* =========================================================
     SELECCIONAR CLIENTE
  ========================================================= */
  const seleccionarCliente = useCallback(
    async (clienteId) => {
      if (!clienteId) {
        try { localStorage.removeItem(CLIENT_CONTEXT_STORAGE_KEY); } catch {}
        setClienteSeleccionadoId(null);
        setClienteInfo(null);
        setOrdenadores([]);
        setPcActivo(null);
        setMaintenanceFocus(null);
        setZabbixByPc({});
        setEstado({
          mantenimientosPorPc: {},
          formularios: {},
          infoAdicionalEdit: {},
        });
        return;
      }

      if (
        isTecnico() &&
        effectiveAccess.loaded &&
        !effectiveAccess.allClients &&
        !effectiveAccess.clientIds.includes(Number(clienteId))
      ) {
        try { localStorage.removeItem(CLIENT_CONTEXT_STORAGE_KEY); } catch {}
        setClienteSeleccionadoId(null);
        setClienteInfo(null);
        return;
      }

      const targetClient = clientes.find((client) => Number(client.id) === Number(clienteId));
      if (targetClient && Number(targetClient.es_cliente_mantenimiento) !== 1) {
        try { localStorage.removeItem(CLIENT_CONTEXT_STORAGE_KEY); } catch {}
        setClienteSeleccionadoId(null);
        setClienteInfo(null);
        setOrdenadores([]);
        return;
      }

      setLoadingCliente(true);
      setMaintenanceFocus(null);
      setClienteSeleccionadoId(clienteId);
      try { localStorage.setItem(CLIENT_CONTEXT_STORAGE_KEY, String(clienteId)); } catch {}

      try {
        const infoRes = await getCliente(clienteId);
        const info = infoRes.data;
        setClienteInfo(info);

        const pcsRes = await getOrdenadoresPorCliente(clienteId);
        const pcs = pcsRes.data || [];
        setOrdenadores(pcs);

        const mantRes = await getMantenimientosPorCliente(clienteId);
        const mapa = mantRes.data || {};

        const mantenimientosPorPc = {};
        const formularios = {};
        const infoAdicionalEdit = {};

        pcs.forEach((pc) => {
          const mant = mapa[pc.id] || [];
          mantenimientosPorPc[pc.id] = [...mant].sort((a, b) =>
            String(b.fecha).localeCompare(String(a.fecha))
          );

          formularios[pc.id] = {
            fecha: new Date().toISOString().slice(0, 10),
            realizado: "1",
            status: "completed",
            follow_up_date: "",
            observaciones: "",
            incidencias: "",
            tecnico: localStorage.getItem("usuario") || "",
          };

          infoAdicionalEdit[pc.id] = pc.info_adicional || "";
        });

        setEstado({
          mantenimientosPorPc,
          formularios,
          infoAdicionalEdit,
        });

        const pendiente = pcs.find((pc) => {
          const lista = mantenimientosPorPc[pc.id] || [];
          return !lista.some(
            (m) => Number(m.realizado) === 1 && esDelMesActual(m.fecha)
          );
        });

        if (pendiente) setPcActivo(pendiente.id);
        else if (pcs.length > 0) setPcActivo(pcs[0].id);
        else setPcActivo(null);

        if (info?.codigo_cliente) {
          await refreshZabbixCliente(info.codigo_cliente);
        }
      } catch (err) {
        console.error("Error cargando cliente:", err);
      } finally {
        setLoadingCliente(false);
      }
    },
    [refreshZabbixCliente, effectiveAccess, clientes]
  );

  // La lista y los permisos se cargan de forma asíncrona; después restauramos
  // el ámbito persistido para recuperar también sus datos de trabajo.
  useEffect(() => {
    if (!effectiveAccess.loaded || clientContextHydrated) return;
    setClientContextHydrated(true);
    if (clienteSeleccionadoId) seleccionarCliente(clienteSeleccionadoId);
  }, [effectiveAccess.loaded, clientContextHydrated, clienteSeleccionadoId, seleccionarCliente]);

  // Si se desactiva el mantenimiento desde la ficha, el contexto operativo no
  // puede conservar ese cliente seleccionado al cambiar de módulo.
  useEffect(() => {
    if (!clienteSeleccionadoId) return;
    const selected = clientes.find((client) => Number(client.id) === Number(clienteSeleccionadoId));
    if (selected && Number(selected.es_cliente_mantenimiento) !== 1) seleccionarCliente(null);
  }, [clientes, clienteSeleccionadoId, seleccionarCliente]);

  /* =========================================================
     ACCIONES
  ========================================================= */
  const guardarMantenimiento = useCallback(
    async (pcId) => {
      const datos = estado.formularios[pcId];

      if (!datos?.tecnico) {
        throw new Error("Introduce el nombre del técnico.");
      }

      try {
        await crearMantenimiento({
          ordenador_id: pcId,
          ...datos,
          realizado: datos.status === "completed" ? 1 : 0,
        });

        const mantPc = await getMantenimientosPorPc(pcId);
        const nuevaListaPc = Array.isArray(mantPc.data) ? mantPc.data : [];

        setEstado((prev) => ({
          ...prev,
          mantenimientosPorPc: {
            ...prev.mantenimientosPorPc,
            [pcId]: nuevaListaPc,
          },
          formularios: {
            ...prev.formularios,
            [pcId]: {
              fecha: new Date().toISOString().slice(0, 10),
              realizado: "1",
              status: "completed",
              follow_up_date: "",
              observaciones: "",
              incidencias: "",
              tecnico: datos.tecnico,
            },
          },
        }));

        await refreshPendientes();

        return true;
      } catch (err) {
        console.error("Error guardando mantenimiento:", err);
        throw new Error(err?.response?.data?.error || err?.message || "Error guardando mantenimiento");
      }
    },
    [estado.formularios, refreshPendientes]
  );

  // Reintento manual seguro: normalmente el informe ya se genera al guardar
  // el último equipo, pero esta acción permite comprobar/reintentar el cierre
  // sin depender de una pantalla o de un técnico concreto.
  const finalizarCliente = useCallback(async () => {
    if (!clienteSeleccionadoId) throw new Error("Selecciona un cliente.");
    const hoy = new Date();
    const mesActual = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;
    const response = await verificarMantenimientoMes(clienteSeleccionadoId, mesActual);
    await refreshPendientes();
    if (!response.data?.success) throw new Error(response.data?.mensaje || "Aún faltan mantenimientos.");
    return response.data;
  }, [clienteSeleccionadoId, refreshPendientes]);

  /* =========================================================
     PC ACTUAL
  ========================================================= */
  const pcActual = useMemo(() => {
    if (!pcActivo) return null;
    return ordenadores.find((p) => p.id === pcActivo) || null;
  }, [ordenadores, pcActivo]);

  /* =========================================================
     CONTEXT VALUE
  ========================================================= */
  const value = useMemo(
    () => ({
      activeModule,
      setActiveModule,

      clientes,
      setClientes,
      loadingClientes,
      refreshClientes,
      effectiveAccess,

      clienteSeleccionadoId,
      clienteInfo,
      seleccionarCliente,

      ordenadores,
      pcActivo,
      setPcActivo,
      maintenanceFocus,
      setMaintenanceFocus,
      pcActual,
      refreshOrdenadores,

      pendientes,
      agrupadosPendientes,
      loadingPendientes,
      refreshPendientes,

      estado,
      setEstado,
      tieneMantenimientoMesActual,

      clienteTagZabbix,
      zabbixByPc,
      loadingZabbix,
      refreshZabbixCliente,

      guardarMantenimiento,
      finalizarCliente,

      loadingCliente,
    }),
    [
      activeModule,
      clientes,
      loadingClientes,
      refreshClientes,
      effectiveAccess,
      clienteSeleccionadoId,
      clienteInfo,
      seleccionarCliente,
      ordenadores,
      pcActivo,
      maintenanceFocus,
      pcActual,
      refreshOrdenadores,
      pendientes,
      agrupadosPendientes,
      loadingPendientes,
      refreshPendientes,
      estado,
      tieneMantenimientoMesActual,
      clienteTagZabbix,
      zabbixByPc,
      loadingZabbix,
      refreshZabbixCliente,
      guardarMantenimiento,
      finalizarCliente,
      loadingCliente,
    ]
  );

  return (
    <ClienteContext.Provider value={value}>
      {children}
    </ClienteContext.Provider>
  );
}

export function useCliente() {
  const ctx = useContext(ClienteContext);
  if (!ctx) {
    throw new Error("useCliente debe usarse dentro de <ClienteProvider>");
  }
  return ctx;
}
